import { useEffect, useState, useMemo, useCallback } from 'react'
import { Users, Search, Phone, Briefcase, Plus, Pencil, Trash2, FileText, QrCode } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useYetki } from '@/hooks/useYetki'
import { YETKILER } from '@/lib/yetkiler'
import { tumPersonelleriGetir, personelSil } from '@/lib/services/personel-service'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { bugunkuTumKayitlar, sonKayitHaritasi, type Kayit } from '@/lib/services/kayit-service'
import { PersonelFormModal } from '@/pages/boss/personel/PersonelFormModal'
import { SetupTokenModal } from '@/pages/boss/personel/SetupTokenModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { BelgelerModal } from '@/components/belge/BelgelerModal'
import { YoneticiPersonelDetayModal } from './YoneticiPersonelDetayModal'
import type { Vardiyali, Pozisyon, Sube, KullaniciPersonel } from '@/types'

export function YoneticiPersonelPage() {
  const { kullanici, firebaseUser } = useAuth()
  const canEkle = useYetki(YETKILER.PERSONEL_EKLE)
  const canDuzenle = useYetki(YETKILER.PERSONEL_DUZENLE)
  const canPasiflestir = useYetki(YETKILER.PERSONEL_PASIFLESTIR)
  const canBelgeIndir = useYetki([
    YETKILER.PERSONEL_BELGE_INDIR,
    YETKILER.PERSONEL_BELGE_YUKLE,
    YETKILER.PERSONEL_BELGE_SIL,
  ])
  const canQR = useYetki(YETKILER.PERSONEL_QR_OLUSTUR)

  const benimSubeIdleri = useMemo<string[] | null>(() => {
    if (kullanici?.rol === 'boss') return null
    if (kullanici?.rol !== 'personel') return []
    return (kullanici as KullaniciPersonel).subeIdleri ?? []
  }, [kullanici])

  const [personeller, setPersoneller] = useState<Vardiyali[]>([])
  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [bugunkuKayitlar, setBugunkuKayitlar] = useState<Kayit[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [arama, setArama] = useState('')

  const [secili, setSecili] = useState<Vardiyali | null>(null)
  const [formAcik, setFormAcik] = useState(false)
  const [duzenlenenPersonel, setDuzenlenenPersonel] = useState<Vardiyali | null>(null)
  const [silOnayPersonel, setSilOnayPersonel] = useState<Vardiyali | null>(null)
  const [siliniyor, setSiliniyor] = useState(false)
  const [belgelerPersonel, setBelgelerPersonel] = useState<Vardiyali | null>(null)
  const [qrPersonel, setQrPersonel] = useState<Vardiyali | null>(null)

  const yukle = useCallback(async () => {
    try {
      setYukleniyor(true)
      const [p, poz, sub, kayit] = await Promise.all([
        tumPersonelleriGetir(),
        tumPozisyonlariGetir(),
        tumSubeleriGetir(),
        bugunkuTumKayitlar(),
      ])

      const aktifPersoneller = p.filter(x => x.aktif)
      const benimPersoneller = benimSubeIdleri === null
        ? aktifPersoneller
        : aktifPersoneller.filter(x =>
            (x.subeIdleri ?? []).some(id => benimSubeIdleri.includes(id))
          )
      const benimSubeler = benimSubeIdleri === null
        ? sub
        : sub.filter(s => benimSubeIdleri.includes(s.id))
      const benimKayitlar = benimSubeIdleri === null
        ? kayit
        : kayit.filter(k => benimSubeIdleri.includes(k.subeId))

      setPersoneller(benimPersoneller)
      setPozisyonlar(poz)
      setSubeler(benimSubeler)
      setBugunkuKayitlar(benimKayitlar)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Personeller yüklenemedi.')
    } finally {
      setYukleniyor(false)
    }
  }, [benimSubeIdleri])

  useEffect(() => { yukle() }, [yukle])

  const pozisyonHaritasi = useMemo(() => {
    const m = new Map<string, string>()
    pozisyonlar.forEach(p => m.set(p.id, p.isim))
    return m
  }, [pozisyonlar])

  const subeHaritasi = useMemo(() => {
    const m = new Map<string, Sube>()
    subeler.forEach(s => m.set(s.id, s))
    return m
  }, [subeler])

  const sonKayitlar = useMemo(() => sonKayitHaritasi(bugunkuKayitlar), [bugunkuKayitlar])

  const filtreli = useMemo(() => {
    const q = arama.trim().toLowerCase()
    if (!q) return personeller
    return personeller.filter(p =>
      p.isim.toLowerCase().includes(q) || (p.telefon ?? '').includes(q)
    )
  }, [personeller, arama])

  function durum(p: Vardiyali): 'mesaide' | 'yok' {
    return sonKayitlar.get(p.id)?.tip === 'giris' ? 'mesaide' : 'yok'
  }

  function yeniEkle() {
    setDuzenlenenPersonel(null)
    setFormAcik(true)
  }

  function duzenle(p: Vardiyali) {
    setDuzenlenenPersonel(p)
    setFormAcik(true)
  }

  async function silOnaylandi() {
    if (!silOnayPersonel || !firebaseUser) return
    setSiliniyor(true)
    try {
      await personelSil(silOnayPersonel.id, firebaseUser.uid, 'yonetici', kullanici?.isim)
      setSilOnayPersonel(null)
      await yukle()
    } catch (err) {
      console.error(err)
      setHata('Personel pasifleştirilemedi.')
    } finally {
      setSiliniyor(false)
    }
  }

  if (yukleniyor) {
    return (
      <div className="card text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
        <p className="text-gray-600">Yükleniyor...</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users className="w-6 h-6 text-brand-600" />
        <h1 className="text-2xl font-bold text-gray-900">Personel</h1>
        <span className="text-sm text-gray-500 ml-1">({filtreli.length})</span>
        {canEkle && (
          <button onClick={yeniEkle} className="ml-auto btn-primary inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Yeni
          </button>
        )}
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
          {hata}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={arama}
          onChange={(e) => setArama(e.target.value)}
          placeholder="İsim veya telefon ara..."
          className="input-base pl-9"
        />
      </div>

      {filtreli.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          {personeller.length === 0
            ? 'Şubenizde personel yok.'
            : 'Arama sonucu bulunamadı.'}
        </div>
      ) : (
        <div className="grid gap-2">
          {filtreli.map(p => {
            const d = durum(p)
            const pozisyonAdi = (p.pozisyonIdleri ?? [])
              .map(id => pozisyonHaritasi.get(id))
              .filter(Boolean)
              .join(', ') || '—'
            const subeAdlari = (p.subeIdleri ?? [])
              .map(id => subeHaritasi.get(id)?.isim)
              .filter(Boolean)
              .join(' · ') || '—'

            return (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => setSecili(p)}
                onKeyDown={(e) => { if (e.key === 'Enter') setSecili(p) }}
                className="card text-left hover:border-brand-300 hover:shadow-sm transition-all p-3 cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-gray-900 truncate">{p.isim}</p>
                      {d === 'mesaide' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase whitespace-nowrap bg-green-100 text-green-700">
                          Mesaide
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
                      <Briefcase className="w-3 h-3 flex-shrink-0" /> {pozisyonAdi}
                    </p>
                    {subeler.length > 1 && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{subeAdlari}</p>
                    )}
                    {p.telefon && (
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3 flex-shrink-0" /> {p.telefon}
                      </p>
                    )}
                  </div>
                  {(p as Vardiyali & { profilFotoUrl?: string }).profilFotoUrl ? (
                    <img
                      src={(p as Vardiyali & { profilFotoUrl?: string }).profilFotoUrl}
                      alt={p.isim}
                      className="w-28 h-28 rounded-full object-cover border-2 border-gray-200 flex-shrink-0 self-center"
                    />
                  ) : (
                    <div className="w-28 h-28 rounded-full bg-gradient-to-br from-brand-400 to-indigo-500 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0 self-center">
                      {p.isim.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {(canQR || canBelgeIndir || canDuzenle || canPasiflestir) && (
                    <div className="flex flex-col gap-1.5">
                      {canQR && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setQrPersonel(p) }}
                          className="p-2 hover:bg-brand-50 rounded-lg text-gray-600 hover:text-brand-600 transition-colors"
                          title="Setup QR"
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                      )}
                      {canBelgeIndir && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setBelgelerPersonel(p) }}
                          className="p-2 hover:bg-purple-50 rounded-lg text-gray-600 hover:text-purple-600 transition-colors"
                          title="Belgeler"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      )}
                      {canDuzenle && (
                        <button
                          onClick={(e) => { e.stopPropagation(); duzenle(p) }}
                          className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900 transition-colors"
                          title="Düzenle"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {canPasiflestir && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSilOnayPersonel(p) }}
                          className="p-2 hover:bg-red-50 rounded-lg text-gray-600 hover:text-red-600 transition-colors"
                          title="Pasif Yap"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <YoneticiPersonelDetayModal
        acik={!!secili}
        personel={secili}
        pozisyonHaritasi={pozisyonHaritasi}
        subeHaritasi={subeHaritasi}
        bugunkuDurum={secili ? durum(secili) : 'yok'}
        onKapat={() => setSecili(null)}
      />

      <PersonelFormModal
        acik={formAcik}
        personel={duzenlenenPersonel}
        onKapat={() => setFormAcik(false)}
        onKaydedildi={() => {
          setFormAcik(false)
          setDuzenlenenPersonel(null)
          yukle()
        }}
      />

      <ConfirmDialog
        acik={!!silOnayPersonel}
        baslik="Personel Pasif Yapılsın mı?"
        mesaj={`${silOnayPersonel?.isim ?? ''} pasifleştirilecek. Liste ve raporlardan kaldırılır, mevcut kayıtlar silinmez. BOSS sonradan geri yükleyebilir.`}
        onayMetni="Pasif Yap"
        tehlikeli
        onOnay={silOnaylandi}
        onIptal={() => !siliniyor && setSilOnayPersonel(null)}
      />

      <BelgelerModal
        acik={!!belgelerPersonel}
        personel={belgelerPersonel}
        onKapat={() => setBelgelerPersonel(null)}
      />

      <SetupTokenModal
        acik={!!qrPersonel}
        personelId={qrPersonel?.id ?? null}
        personelIsim={qrPersonel?.isim ?? ''}
        onKapat={() => setQrPersonel(null)}
      />
    </div>
  )
}
